import { useMemo, useEffect, useEffectEvent } from 'react';
import { useQuery, skipToken } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import { INITIAL_VALUES as OFFER_EVENT_INITIAL_VALUES } from 'offer/forms/OfferEventForm/constants';
import {
    IContentTypeValue,
    IOfferEventDetailFragment,
    IOfferEventType,
    IOfferTypeChoices,
    ExtraCategoriesDocument,
    GroupsDocument,
} from 'graphql/types';
import { TCreateOfferFormValues, TOfferEventFormValues } from 'offer/types';
import { getOfferEventFormData } from 'offer/utils/offerEvent';
import { getGroupConditionsFilterInputs } from 'user/utils/groups';
import { getExtraCategoryInitialFormValues } from 'organisation/utils/extraCategory';
import { useApolloError } from 'common/hooks/useApolloError';
import { TExtraCategoryInput } from 'organisation/types';
import { IGroupConditionInput } from 'user/types';
import { useHashMatch } from 'route/hooks/useHashMatch';

interface IProps {
    edit?: boolean;
    offerEvent?: IOfferEventDetailFragment;
    includeOfferFields?: boolean;
    initialExtraFields?: string;
    offerEventType: IOfferEventType;
    isOfferDetailDrawer?: boolean;
    hasServiceProvider?: boolean;
    currentUserIsAuthor?: boolean;
}

export const useOfferEventInitialValues = ({
    edit,
    offerEvent,
    offerEventType,
    includeOfferFields,
    isOfferDetailDrawer,
    hasServiceProvider,
    currentUserIsAuthor,
}: IProps) => {
    const isOfferDetailDrawerPage =
        !!useHashMatch('OFFER_DETAIL_DRAWER') || isOfferDetailDrawer;

    const { showApolloError } = useApolloError();

    const {
        data: extraCategoryData,
        loading: extraCategoryLoading,
        error: extraCategoryError,
    } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType: IContentTypeValue.Offer },
    });

    const {
        data: groupsData,
        loading: loadingGroups,
        error: groupsError,
    } = useQuery(
        GroupsDocument,
        // If the current user is author it checks if it has one or more groups
        // If it has one group it will automatically add it to the group condition filter
        // If it has more than one group it does not add anything automatically
        currentUserIsAuthor
            ? {
                  notifyOnNetworkStatusChange: true,
                  variables: { first: 2, offset: 0 },
              }
            : skipToken
    );

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (extraCategoryError) {
            onError(extraCategoryError);
        }

        if (groupsError) {
            onError(groupsError);
        }
    }, [extraCategoryError, groupsError]);

    const initialValues = useMemo(() => {
        let offerInitialValues:
            | (TCreateOfferFormValues & {
                  extraCategories: TExtraCategoryInput;
                  groupConditions: IGroupConditionInput[];
              })
            | Record<string, never> = {};

        const { extraCategories } = extraCategoryData || {};

        if (includeOfferFields) {
            const groupConditions: IGroupConditionInput[] = [];

            // Add initial groups for authors if only one group exists
            if (!edit && currentUserIsAuthor) {
                const groups =
                    groupsData?.paginatedGroups?.edges
                        .map((edge) => edge?.node)
                        .filter(Boolean) || [];

                if (groups.length === 1) {
                    const groupCondition: IGroupConditionInput = {
                        id: '',
                        groups: groups.map((group) => group.id),
                        extraCategoryValues: [],
                        minMaxFilters: [],
                    };

                    groupConditions.push(groupCondition);
                }
            }

            offerInitialValues = {
                title: '',
                titleColor: '',
                hasDetailPage: false,
                extraCategories: getExtraCategoryInitialFormValues(
                    extraCategories,
                    []
                ),
                groupConditions,
            };
        }

        if (edit && !!offerEvent) {
            const {
                title,
                offer,
                trainers: trainerConnection,
                trainerApproval,
                minSubscriptions,
                maxSubscriptions,
                price,
                descriptionBeforeSubscription,
                descriptionAfterSubscription,
                accessTrainings,
                accessExternalContents,
                accessCourses,
                showParticipantList,
                publicParticipantList,
                publish,
                waitingList,
                type,
                certificate,
                accessScorms,
                accessVideos,
                scoreThreshold,
                groupManagersCanEnrollParticipants,
            } = offerEvent;

            if (includeOfferFields) {
                offerInitialValues = {
                    title: offer.title,
                    titleColor: offer.titleColor?.replace('#', '') || '',
                    overviewImage: offer.overviewImage,
                    hasDetailPage: undefined, // After creation of an offer this should not be changed
                    extraCategories: getExtraCategoryInitialFormValues(
                        extraCategories,
                        offer.extraCategoryValues
                    ),
                    groupConditions: getGroupConditionsFilterInputs(
                        offer.groupConditions || []
                    ),
                };
            }

            const offerEventInitialValues = {
                offerEventTitle: title,
                trainers: trainerConnection?.edges
                    .map((edge) => edge?.node?.id)
                    .filter(Boolean),
                trainerApproval,
                minSubscriptions: minSubscriptions ?? 0,
                maxSubscriptions: maxSubscriptions ?? 0,
                price,
                descriptionBeforeSubscription,
                descriptionAfterSubscription,
                accessTrainings: accessTrainings.edges
                    .map((trainingEdge) => trainingEdge?.node?.id)
                    .filter(Boolean),
                accessExternalContents: accessExternalContents.edges
                    .map((externalContentEdge) => externalContentEdge?.node?.id)
                    .filter(Boolean),
                accessCourses: accessCourses.edges
                    .map((courseEdge) => courseEdge?.node?.id)
                    .filter(Boolean),
                showParticipantList,
                publicParticipantList,
                waitingList,
                certificate,
                publish,
                type: type || IOfferTypeChoices.Training,
                accessScorms: accessScorms.edges
                    .map((accessScormsEdge) => accessScormsEdge?.node?.id)
                    .filter(Boolean),
                accessVideos: accessVideos.edges
                    .map((accessVideosEdge) => accessVideosEdge?.node?.id)
                    .filter(Boolean),
                scoreThreshold: scoreThreshold || 0,
                groupManagersCanEnrollParticipants,
                ...getOfferEventFormData(type, offerEvent, hasServiceProvider)
                    .initialValues,
            } as TOfferEventFormValues;

            if (isOfferDetailDrawerPage) {
                return {
                    ...offerInitialValues,
                    ...offerEventInitialValues,
                } as TOfferEventFormValues;
            }

            return offerEventInitialValues as TOfferEventFormValues;
        }

        return {
            ...offerInitialValues,
            publish: true,
            ...OFFER_EVENT_INITIAL_VALUES,
            ...getOfferEventFormData(
                offerEventType,
                undefined,
                hasServiceProvider
            ).initialValues,
        } as TOfferEventFormValues;
    }, [
        edit,
        offerEvent,
        offerEventType,
        includeOfferFields,
        isOfferDetailDrawerPage,
        extraCategoryData,
        groupsData,
    ]);

    return {
        initialValues,
        loading: extraCategoryLoading || loadingGroups,
    };
};
